import { decomposeColor, recomposeColor, rgbToHex } from '@mui/system';
import { NEGATIVE_CONTROL, POSITIVE_CONTROL } from 'components/Procedure/constants';
import {
  capitalize,
  compact,
  curry,
  filter,
  find,
  first,
  fromPairs,
  indexOf,
  isEmpty,
  isEqual,
  isNumber,
  join,
  map,
  pickBy,
  replace,
  some,
  split,
  startCase,
  toPairs,
  values,
} from 'es-toolkit/compat';
import { Accession } from 'interfaces/accession';
import { QueryFilter } from 'interfaces/cohort';
import { Procedure } from 'interfaces/procedure';
import { ProceduresFieldsContext } from 'interfaces/procedure/fields/helpers';
import { Slide } from 'interfaces/slide';
import numeral from 'numeral';
import { resolveApiHost } from 'utils/api';
import { UiSettings } from 'utils/queryHooks/uiConstantsHooks';

declare const IMAGE_HOST: string;
declare const STATIC_IMAGE_HOST: string;

export interface SlideTileUrlOptions {
  isStagingLab?: boolean;
}

const getSlideAssetBaseUrl = (slide: Pick<Slide, 'id' | 'labId'>, options?: SlideTileUrlOptions) => {
  const labId = slide.labId;
  if (options?.isStagingLab) {
    return `${resolveApiHost()}/slides/staging/${labId}/${slide.id}`;
  }
  return `${STATIC_IMAGE_HOST}/${labId}/${slide.id}`;
};

export function getSlideThumbnailUrl(slide: Pick<Slide, 'id' | 'labId'>, options?: SlideTileUrlOptions) {
  return `${getSlideAssetBaseUrl(slide, options)}/${slide.id}_thumbnail.png`;
}

export function getSlideOriginalUrl(slide: Pick<Slide, 'id' | 'labId'>, options?: SlideTileUrlOptions) {
  return `${getSlideAssetBaseUrl(slide, options)}/${slide.id}_thumbnail_big.png`;
}

export function getSlideChannelDziUrl(
  slide: Pick<Slide, 'id' | 'labId'>,
  channelIndex: number | string,
  options?: SlideTileUrlOptions
) {
  if (options?.isStagingLab) {
    return `${getSlideAssetBaseUrl(slide, options)}/tiles.dzi`;
  }
  return `${getSlideAssetBaseUrl(slide, options)}/tiles_${channelIndex}.dzi`;
}

export function getSlideBaseDziUrl(slide: Pick<Slide, 'id' | 'labId'>, options?: SlideTileUrlOptions) {
  return `${getSlideAssetBaseUrl(slide, options)}/tiles.dzi`;
}

export function getSlideChannelTilesBaseUrl(
  slide: Pick<Slide, 'id' | 'labId'>,
  channelIndex: number | string,
  options?: SlideTileUrlOptions
) {
  if (options?.isStagingLab) {
    return `${getSlideAssetBaseUrl(slide, options)}/tiles_files/${channelIndex}`;
  }
  return `${getSlideAssetBaseUrl(slide, options)}/tiles_${channelIndex}_files`;
}

export function getAccessionIdentifier(accession?: Accession) {
  return first(split(accession?.accessionData?.accessionId, '-'));
}

export function getProcedureIdentifier(procedure?: Procedure) {
  // we no longer want to fallback on the first slide label, if the procedure needs to have a specific label
  // then its respective procedure_results.label field in the db needs to be updated.
  return procedure?.label || procedure?.id?.toString() || null;
}

const uuidv4ReplaceRegex = /[018]/g;

export function uuidv4(): string {
  // @ts-ignore
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(uuidv4ReplaceRegex, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

export function getSlideMacroUrl(slide: Slide, formatExtensions: Record<string, string>) {
  return `${IMAGE_HOST}/${slide.id}${
    formatExtensions[slide.format]
  }.dzi/macro.png?force_portrait=true&prefer_static=true`;
}

export function toPercentage(fraction: number, fixedDigitsAfterDecimalPoint = 2) {
  const percentage = `${(fraction * 100).toFixed(fixedDigitsAfterDecimalPoint)}%`;
  return percentage === '0.00%' ? '0%' : percentage;
}

export function divideToPercentage(num1: number, num2: number) {
  return num2 !== 0 ? toPercentage(num1 / num2) : toPercentage(0);
}

export function formatNumber(n: number, format?: string, forExport?: boolean) {
  if (format) {
    let formattedNumber = numeral(n).format(format);

    if (forExport) {
      formattedNumber = replace(formattedNumber, '%', '');
    }

    return formattedNumber;
  }
  if (n === 0) return numeral(n).format('0');
  if (n > -1 && n < 1) return numeral(n).format('0.000e+0');
  return numeral(n).format('0.000');
}

export function formatSlideResolution(resolution: number) {
  return numeral(resolution).format('0.0[0000]') + ' µm/pixel';
}

export function humanize(str: string | null | undefined) {
  return capitalize(startCase(str || ''));
}

export function formatDataKeyToText(key: string) {
  const keyWithoutPrefix = removePrefixFromDataKey(key);
  return humanize(replace(keyWithoutPrefix, /_/g, ' '));
}

export function removePrefixFromDataKey(key: string) {
  return key.substring(indexOf(key, '.') + 1);
}

export const displayFeatureName = curry((uiSettings: UiSettings, inferredKey: string) => {
  return (
    uiSettings?.webappFeaturesConfig[removePrefixFromDataKey(inferredKey)]?.displayName ||
    formatDataKeyToText(inferredKey)
  );
});

export const NULL_VALUE_DISPLAY_NAME = 'Unspecified';

export const displayProcedureFieldValueName = curry(
  (procedureFieldContext: ProceduresFieldsContext, inferredKey: string, inferredValue: string | number | boolean) => {
    const { cancerTypes, biopsySiteTypes } = procedureFieldContext;

    if (
      inferredValue === null ||
      inferredValue === undefined ||
      inferredValue === 'null' ||
      inferredValue === 'undefined'
    ) {
      return NULL_VALUE_DISPLAY_NAME;
    }
    if (inferredKey === 'cancerTypeId') {
      return find(cancerTypes, { id: Number(inferredValue) })?.displayName;
    }
    if (inferredKey === 'biopsySiteId') {
      return find(biopsySiteTypes, { id: Number(inferredValue) })?.displayName;
    }
    if (inferredKey === 'biopsyType') {
      const biopsyTypeOption = find(procedureFieldContext.enumDisplayNames?.biopsyType, {
        value: String(inferredValue),
      });
      return biopsyTypeOption?.label ?? inferredValue;
    }
    if (inferredKey === 'qcFailed') {
      // the qcFailed value is a string of 'true'/'false' and we want to show 'Unusable' or 'Usable' instead of 'true' or 'false'
      return inferredValue === 'true' ? 'Unusable' : 'Usable';
    }

    return inferredValue;
  }
);

export function formatRangeQueryStringToText(rangeQueryString: string) {
  if (!rangeQueryString) return '';
  const [start, end] = rangeQueryString.split('~');
  if (!end) return `=< ${start}`;
  if (!start) return `>= ${end}`;
  return `${start} - ${end}`;
}

export function formatRangeObjectToText(range: { start: string; end: string }) {
  if (!range) {
    return '';
  }
  if (!range.start) return `=< ${range.end}`;
  if (!range.end) return `>= ${range.start}`;
  return `${range.start} - ${range.end}`;
}
export type Prepend = {
  <T>(item: T, arr: T[]): T[];
  <T>(item: T): (arr: T[]) => T[]; // can be curried too
};

// function that prepends an item to an array, pure version of array.unshift
export const prepend: Prepend = curry(<T>(item: T, arr: T[]) => [item, ...arr]);

export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const hexCatchingRegex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;

export const isHexString = (hex: string) => hexCatchingRegex.test(hex);

export const hexToRgb = (hex: string): [number, number, number] => {
  try {
    const decomposed = decomposeColor(hex);
    if (decomposed.type !== 'rgb') {
      console.error('Expected the color to decompose to RGB.');
    }
    return decomposed.values as [number, number, number];
  } catch (error) {
    console.error('Error converting hex to RGB:', error);
    return [0, 0, 0]; // Fallback to black in case of error
  }
};

export const rgbaToStringRgba = (rgba: [number, number, number, number?]): string => {
  const [r, g, b, a = 1] = rgba;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

export const convertCssColorNameToHex = (colorName: string): string | null => {
  try {
    var ctx = document.createElement('canvas').getContext('2d');
    ctx.fillStyle = colorName;
    // See https://html.spec.whatwg.org/multipage/canvas.html#dom-context-2d-fillstyle - this is actually a getter according to the spec
    const convertedColor = ctx.fillStyle;

    return isHexString(convertedColor as string) ? convertedColor : null;
  } catch (error) {
    console.error('Error converting color name to hex:', error);
    return null;
  }
};

export const getColorHex = (color: any) => {
  const colorString = typeof color === 'string' ? color : color?.hex;
  try {
    if (isHexString(colorString)) {
      return colorString;
    }

    const hexColorFromName = convertCssColorNameToHex(colorString);

    if (!isHexString(hexColorFromName as string)) {
      console.error('Invalid color string:', colorString);
      return null;
    }

    return hexColorFromName;
  } catch (error) {
    console.error('Error converting color to hex:', error);
    return null;
  }
};

const hashHexRegex = /#/g;

export const invertHexColor = (hexColorString: string) => {
  const colorValue = parseInt(replace(hexColorString, hashHexRegex, ''), 16);
  const invertedColorValue = colorValue ^ 0xffffff;
  const invertedColorStringValue = invertedColorValue.toString(16).padStart(6, '0').slice(-6);
  return `#${invertedColorStringValue}`;
};

/**
 * Converts an array of RGB values ([r, g, b], each 0–255) to HSL.
 * Returns an object with h (0–360), s (0–100) and l (0–100).
 */
function rgbToHslFromMUI(rgb: [number, number, number]): { h: number; s: number; l: number } {
  let [r, g, b] = map(rgb, (val) => val / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0,
    s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

/**
 * Converts an HSL color (with h in 0–360 and s/l in 0–100) to an array of RGB values ([r, g, b], each 0–255).
 */
function hslToRgbFromMUI(hsl: { h: number; s: number; l: number }): number[] {
  let { h, s, l } = hsl;
  h = h / 360;
  s = s / 100;
  l = l / 100;
  let r: number, g: number, b: number;

  if (s === 0) {
    // Achromatic (gray)
    r = g = b = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Given a hex color, this function reverses the lightness (L) value of its HSL representation.
 * The lightness value is flipped around 50, so that dark colors become light and vice versa.
 * The lightness value is clamped to 25 if it’s exactly 50 to avoid flipping to the same color.
 * It uses MUI’s decomposeColor and recomposeColor utilities together with custom RGB⇄HSL helpers.
 *
 * @param hex - A string representing the original hex color (e.g., "#3498db").
 * @returns A new hex color string with the lightness reversed.
 */
export function reverseLightness(hex: string): string {
  // Decompose the hex color to its RGB components.
  const decomposed = decomposeColor(hex);
  if (decomposed.type !== 'rgb') {
    throw new Error('Expected the color to decompose to RGB.');
  }

  // Convert the RGB array to HSL.
  const hsl = rgbToHslFromMUI(decomposed.values as [number, number, number]);
  // Reverse the lightness value (scale: 0–100).
  const reversedL = hsl.l !== 50 ? 100 - hsl.l : 25; // Halve the lightness if it’s exactly 50 to avoid flipping to the same color.
  const newHsl = { h: hsl.h, s: hsl.s, l: reversedL };
  // Convert the modified HSL back to RGB.
  const newRgb = hslToRgbFromMUI(newHsl) as [number, number, number];
  // Recompose the RGB values into a hex color.
  return rgbToHex(recomposeColor({ type: 'rgb', values: newRgb }));
}

export const splitStringAfterNChars = (longText: string, n: number) =>
  longText?.match(new RegExp(`.{1,${n}}[^ ]* ?`, 'g'));

export const allFiltersHasValue = (filters: Record<string, QueryFilter>) => {
  return some(values(filters), (value: any) => isNumber(value) || !isEmpty(value));
};

export const removeNullValues = (obj: Record<string, any>) => pickBy(obj, (value) => value !== null);

export const getStainStrWithNegPosControl = (stain: string, negativeControl?: boolean, positiveControl?: boolean) => {
  if (negativeControl) {
    return `${stain} ${NEGATIVE_CONTROL}`;
  } else if (positiveControl) {
    return `${stain} ${POSITIVE_CONTROL}`;
  }

  return stain;
};

const SPACE_REGEX = /\s+/g;

export const reduceExtraSpaces = (input: string): string => {
  // Trim the input to remove spaces at the beginning and end
  const trimmedInput = input.trim();

  // Replace multiple spaces in the middle with a single space
  const reducedSpaces = trimmedInput.replace(SPACE_REGEX, ' ');

  return reducedSpaces;
};

/**
 * Given an object and an original version of it, return all fields that have changed, or an empty dictionary in case of equality
 * @param newObject object with changes
 * @param oldObject original object we're comparing to
 * @param compareNullsAndUndefined if true, null and undefined are considered different
 * @returns the fields of newObject that are different from oldObject
 */
export function getFieldChanges<T extends object>(
  newObject: T,
  oldObject: T,
  compareNullsAndUndefined: boolean = false
): Record<string, T[keyof T]> {
  return fromPairs(
    filter(
      toPairs(newObject),
      ([key, value]) =>
        !isEqual(value, oldObject?.[key as keyof T]) &&
        (compareNullsAndUndefined || value !== null || oldObject?.[key as keyof T] !== undefined)
    )
  );
}

/**
 * Converts an SVG string to a data URL.
 * @param svg - The SVG string to convert.
 * @returns A data URL representing the SVG.
 */
export const svgToDataURL = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/**
 * Normalizes whitespace and converts a string to display-oriented title case.
 *
 * This removes repeated spaces before applying lodash `startCase`, so it is
 * more aggressive than a simple first-letter capitalization.
 *
 * Examples:
 * - `"hello   world"` -> `"Hello World"`
 * - `"tumor_cell-intensity1"` -> `"Tumor Cell Intensity 1"`
 * - `"myHTTPValue"` -> `"My HTTP Value"`
 */
export const titleCase = (value: string) => startCase(join(compact(split(value, ' ')), ' '));
